import { useCallback, useEffect, useMemo, useState } from "react";
import { displayCallBackCountdown, displayTextWithOverflow } from "../../../../helper-functions";
import { CallBack } from "../../../../models";
import { Badge } from "react-bootstrap";

interface CallBackSummaryProps {
    callBack: CallBack;
    onClick: (id: number) => void;
    isOnDashboard?: boolean;
}

export function CallBackSummary({ callBack, onClick, isOnDashboard }: Readonly<CallBackSummaryProps>) {
    const { name, scheduledTime, userName, note } = callBack;
    const referral = callBack.case?.referral;

    const getMinutesDiff = useCallback(() => {
        const now = new Date();
        const timeDate = new Date(scheduledTime);
        const diffMs = timeDate.getTime() - now.getTime();
        return Math.floor(diffMs / 1000 / 60);
    }, [scheduledTime]);

    const [minutesDiff, setMinutesDiff] = useState(getMinutesDiff());

    useEffect(() => {
        const interval = setInterval(() => {
            setMinutesDiff(getMinutesDiff());
        }, 1000);
        return () => clearInterval(interval);
    }, [getMinutesDiff]);

    const timerColor = useMemo(() => {
        if (callBack.isComplete) return "white";
        if (minutesDiff > 1) return "success";
        if (minutesDiff > 0) return "warning";
        return "danger";
    }, [callBack.isComplete, minutesDiff]);

    const displayTime = useMemo(() => {
        const date = new Date(scheduledTime);
        return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`;
    }, [scheduledTime]);

    if (!referral) return null;

    return (
        <div className="d-flex mb-2 clickable" onClick={() => onClick(callBack.id)}>
            <div style={{ borderRadius: "5px", width: "40px" }} className={`bg-${timerColor}-faded me-2`} />
            <div className="w-100">
                <div className="d-flex justify-content-between align-items-center">
                    <strong>{name}</strong>
                    <Badge
                        pill
                        bg={timerColor}
                        className={timerColor === "warning" || timerColor === "white" ? "text-dark" : ""}
                    >
                        {callBack.isComplete ? "Completed" : displayCallBackCountdown(minutesDiff)}
                    </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center" style={{ fontSize: "13px" }}>
                    {!!note && <div>{displayTextWithOverflow(note, note, 75)}</div>}
                    {callBack.isPendingFollowUp && !isOnDashboard && <div>Pending Follow-Up</div>}
                </div>
                <div className="text-muted d-flex mt-2 justify-content-between" style={{ fontSize: "13px" }}>
                    {isOnDashboard ? (
                        <>
                            <div className="d-flex">
                                <div className="pe-2">Decedent:</div>
                                <div>
                                    <span className="text-nowrap fw-bold">
                                        {referral.patientFirstName} {referral.patientLastName}
                                    </span>
                                    <br />
                                    <div>{displayTime}</div>
                                </div>
                            </div>
                            <div>
                                <br />
                                <div>{userName}</div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>{displayTime}</div>
                            <div>{userName}</div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
